/* ===================== REMEDIES =====================
   The dashboard's Remedies screen (sidebar key `remedies`, open to
   all roles). Personalised graha-shanti remedies computed from the
   user's LIVE birth chart, not a generic list:

   - usePlanetStrengths (VedAstro Shadbala Pinda + Ishta/Kashta
     scores) decides WHICH grahas are weak or afflicted;
   - the classical REMEDY_BOOK (@astro/vedastro) supplies WHAT the
     traditional remedies for those grahas are — mantra + japa count,
     gemstone, fasting day, charity and practice;
   - the current Mahadasha lord (already on the cached kundli read)
     gets a "Dasha Lord" badge since its remedies act strongest.

   Gated behind having a birth date on file (CTA to My Profile
   otherwise), skeletons while computing, retry state on failure. */
import { appUrl } from '@astro/config'
import { REMEDY_BOOK, remedyFor } from '@astro/vedastro'
import { canAccessTab } from '../roles'
import { useAstrology, usePlanetStrengths } from '../useDashboard'

/** One remedy fact — tiny tracked label over the value, the dashboard's idiom. */
function Fact({ label, value }) {
  return (
    <div>
      <p className="text-[8px] font-bold uppercase tracking-[0.18em] text-stone-400">{label}</p>
      <p className="text-xs font-semibold text-astroDark mt-1 leading-relaxed">{value}</p>
    </div>
  )
}

export default function RemediesTab({ onSwitch, roles }) {
  const { strengths, hasBirthDetails, profileLoading, isLoading, isError, error, refetch } = usePlanetStrengths()
  // Kundli is already cached from the overview/kundli tabs — the Mahadasha
  // lord contextualises which remedy matters most right now.
  const astro = useAstrology()
  const dashaLord = astro?.kundli?.dasa?.maha || null
  const canBook = canAccessTab('appoinments', roles)

  // Weakest / most afflicted first: lowest Shadbala ratio, then worst
  // Kashta-over-Ishta margin. Only grahas with a classical remedy entry.
  const afflicted = strengths
    .filter((s) => s.afflicted && remedyFor(s.name))
    .sort((a, b) => a.ratio - b.ratio || (b.kashta - b.ishta) - (a.kashta - a.ishta))

  /* Meter width: ratio 1.0 = the classical threshold (marked at 2/3 of the
     track), so a planet just past its minimum reads clearly "over the line". */
  const meterWidth = (ratio) => `${Math.min(100, Math.round(ratio * 66.7))}%`

  return (
    <div className="space-y-6">
      {/* ── Remedies console strip ── */}
      <div className="relative overflow-hidden rounded-2xl bg-[#3D0A0A] text-white p-5 shadow-lg shadow-[#3D0A0A]/15">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#D48806]/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-12 left-1/3 w-44 h-44 bg-red-900/40 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-[8px] uppercase font-bold tracking-[0.3em] text-[#D4A017]">Graha Shanti</p>
            <h2 className="text-lg font-royal text-[#FBF3E4] mt-1">Your personalised Vedic remedies</h2>
            <p className="text-[10px] text-white/50 mt-0.5">Computed from your birth chart&rsquo;s planetary strengths — strengthen what the heavens left weak.</p>
          </div>
          {dashaLord && (
            <span className="flex items-center gap-2 border border-white/15 bg-white/5 text-[9px] font-bold uppercase tracking-wider px-3 py-2 rounded-full shrink-0">
              <span className="text-[#D4A017]">{REMEDY_BOOK[dashaLord]?.glyph || '✦'}</span>
              Current Mahadasha · {dashaLord}
            </span>
          )}
        </div>
      </div>

      {/* ── Planetary strength analysis (Shadbala + Ishta/Kashta) ── */}
      <div className="dash-card p-4 sm:p-6">
        <div className="flex flex-wrap justify-between items-center gap-x-4 gap-y-2 mb-5">
          <div>
            <span className="eyebrow text-amber-700">Shadbala Analysis</span>
            <h3 className="text-xl text-astroDark font-royal">Planetary Strength</h3>
          </div>
          {!profileLoading && hasBirthDetails && !isLoading && !isError && afflicted.length > 0 && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700">{afflicted.length} need support</span>
          )}
        </div>

        {profileLoading || isLoading ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <StrengthSkeletonRow key={i} />
            ))}
          </div>
        ) : !hasBirthDetails ? (
          <div className="py-10 text-center">
            <div className="text-4xl mb-3 opacity-40">🪐</div>
            <p className="text-base font-semibold text-astroDark mb-1">Add your birth details</p>
            <p className="text-sm text-stone-400 mb-4">Your date of birth lets us measure each graha&rsquo;s strength in your chart and prescribe the right remedies.</p>
            <button
              onClick={() => onSwitch('myprofile')}
              className="bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all"
            >
              Complete My Profile
            </button>
          </div>
        ) : isError ? (
          <div className="py-10 text-center">
            <div className="text-4xl mb-3 opacity-40">🛰️</div>
            <p className="text-base font-semibold text-astroDark mb-1">Couldn&rsquo;t compute planetary strengths</p>
            <p className="text-sm text-stone-400 mb-4">{error}</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-6 py-3 rounded-full border transition-colors"
              style={{ borderColor: 'rgba(180,83,9,.35)', color: '#B45309', background: 'rgba(180,83,9,.06)' }}
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {strengths.map((s) => (
              <div key={s.name} className="flex items-center gap-3 border border-stone-100 rounded-xl p-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center text-lg shrink-0">
                  {REMEDY_BOOK[s.name]?.glyph || '✦'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <p className="text-sm font-bold text-stone-900">
                      {s.name}
                      {s.name === dashaLord && (
                        <span className="ml-2 text-[8px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#3D0A0A] text-[#D4A017]">Dasha Lord</span>
                      )}
                    </p>
                    <span className={`text-[9px] font-bold uppercase tracking-wide px-2 py-1 rounded-full shrink-0 ${
                      s.afflicted ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-600'
                    }`}>
                      {s.afflicted ? 'Needs Support' : 'Strong'}
                    </span>
                  </div>
                  <div className="relative h-1.5 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full bg-linear-to-r ${s.strong ? 'from-green-400 to-green-500' : 'from-goldAccent to-amber-600'}`}
                      style={{ width: meterWidth(s.ratio) }}
                    />
                    {/* The classical minimum sits at 2/3 of the track. */}
                    <div className="absolute top-0 bottom-0 w-px bg-stone-300" style={{ left: '66.7%' }} />
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[9px] uppercase tracking-wide font-bold text-stone-400">
                    <span className="num">Shadbala {Math.round(s.pinda)} / {s.required} required</span>
                    <span className="num text-green-600/80">Ishta {s.ishta.toFixed(1)}</span>
                    <span className="num text-red-400">Kashta {s.kashta.toFixed(1)}</span>
                  </div>
                </div>
              </div>
            ))}
            <p className="text-[10px] text-stone-400 pt-2">
              Shadbala (six-fold strength, in virupas) and Ishta/Kashta scores computed live from your birth details · Powered by VedAstro
            </p>
          </div>
        )}
      </div>

      {/* ── Recommended remedies for the afflicted grahas ── */}
      {hasBirthDetails && !profileLoading && !isLoading && !isError && (
        <div className="dash-card p-4 sm:p-6">
          <div className="mb-5">
            <span className="eyebrow text-amber-700">Prescribed For You</span>
            <h3 className="text-xl text-astroDark font-royal">Recommended Remedies</h3>
          </div>

          {afflicted.length === 0 ? (
            <div className="py-10 text-center">
              <div className="text-4xl mb-3 opacity-40">🙏</div>
              <p className="text-base font-semibold text-astroDark mb-1">All grahas stand strong in your chart</p>
              <p className="text-sm text-stone-400">Every planet clears its classical strength threshold — keep up your regular practice.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {afflicted.map((s) => {
                const r = remedyFor(s.name)
                return (
                  <div key={s.name} className="border border-amber-900/10 rounded-xl p-4 bg-amber-50/40 hover:shadow-md hover:border-[#D4A017]/40 transition-all flex flex-col">
                    <div className="flex items-center gap-3 mb-1">
                      <div className="w-11 h-11 rounded-xl bg-white border border-amber-100 flex items-center justify-center text-xl shrink-0 shadow-sm">{r.glyph}</div>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-stone-900">
                          {s.name} Shanti
                          {s.name === dashaLord && (
                            <span className="ml-2 text-[8px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#3D0A0A] text-[#D4A017]">Dasha Lord</span>
                          )}
                        </p>
                        {/* The live reason this graha was flagged. */}
                        <p className="text-[10px] text-stone-500 mt-0.5">
                          {!s.strong
                            ? `Below strength — Shadbala ${Math.round(s.pinda)} of ${s.required} needed`
                            : `Kashta ${s.kashta.toFixed(1)} outweighs Ishta ${s.ishta.toFixed(1)}`}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-3 mt-3 pt-3 border-t border-amber-900/10">
                      <Fact label="Beeja Mantra" value={`${r.mantra} — ${r.japa}`} />
                      <div className="grid grid-cols-2 gap-3">
                        <Fact label="Gemstone" value={r.gemstone} />
                        <Fact label="Deity" value={r.deity} />
                        <Fact label="Fasting" value={r.fasting} />
                        <Fact label="Charity" value={r.charity} />
                      </div>
                      <Fact label="Daily Practice" value={r.practice} />
                    </div>
                    <div className="flex items-center justify-between mt-auto pt-3">
                      <span className="text-[9px] font-bold uppercase tracking-wider text-stone-400">Auspicious day · {r.day}</span>
                      <a
                        href={appUrl('shop')}
                        className="text-[10px] font-bold uppercase tracking-wider text-goldAccent hover:underline"
                      >
                        Shop Gemstones →
                      </a>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-5 pt-4 border-t border-stone-100">
            <p className="text-[10px] text-stone-400 max-w-xl">
              Remedies follow the classical graha-shanti tradition. Gemstones are potent — always confirm with a qualified astrologer before wearing one.
            </p>
            {canBook && (
              <button
                onClick={() => onSwitch('appoinments')}
                className="bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all shrink-0"
              >
                Consult Maharaj Ji
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/* Pulsing placeholder matching one planet-strength row. */
function StrengthSkeletonRow() {
  return (
    <div className="flex items-center gap-3 border border-stone-100 rounded-xl p-3 animate-pulse">
      <div className="w-10 h-10 rounded-xl bg-stone-100 shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="flex items-center justify-between">
          <div className="h-3.5 w-24 bg-stone-100 rounded-full" />
          <div className="h-5 w-20 bg-stone-100 rounded-full" />
        </div>
        <div className="h-1.5 w-full bg-stone-100 rounded-full" />
        <div className="h-2.5 w-48 bg-stone-100 rounded-full" />
      </div>
    </div>
  )
}
